import React, { useState } from "react";
import { coffeeRequest, errorMessage } from "./coffeeApi";
import { queryString, usd, useResource } from "./coffeeUi";
import CoffeeHistory from "./CoffeeHistory";
import CoffeeDetail from "./CoffeeDetail";
import CoffeeReset from "./CoffeeReset";
import CoffeeCharts from "./CoffeeCharts";
import "./coffeeStatistics.css";
export default function CoffeeStatistics({ request = coffeeRequest }) {
  const [filters, setFilters] = useState({
      from: "",
      until: "",
      route: "",
      machine: "",
      operator: "",
    }),
    [page, setPage] = useState(1),
    [refresh, setRefresh] = useState(0),
    [detail, setDetail] = useState(null),
    [reset, setReset] = useState(false),
    [error, setError] = useState(""),
    [exporting, setExporting] = useState(false);
  const query = queryString(filters);
  const options = useResource("/internal/filters", request, refresh);
  const report = useResource(`/internal/summary?${query}`, request, refresh);
  const history = useResource(
    `/internal?${queryString({ ...filters, page })}`,
    request,
    refresh
  );
  const change = (key, value) => {
    setFilters((old) => ({ ...old, [key]: value }));
    setPage(1);
  };
  const retry = () => setRefresh((x) => x + 1);
  const download = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await request(
        `/internal/export?${query}`,
        undefined,
        "GET",
        true
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "estadisticas-cafe.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setExporting(false);
    }
  };
  return (
    <main className="coffee-page">
      <div className="coffee-heading">
        <div>
          <p className="coffee-eyebrow">CONTROL Y ANÁLISIS</p>
          <h1>Estadísticas de Maquinas de Café</h1>
          <p>Consumo y ventas por fecha de cierre del período.</p>
        </div>
        <div className="coffee-actions">
          <button onClick={() => setReset(true)}>Registrar reinicio</button>
          <button
            className="coffee-primary"
            disabled={exporting}
            onClick={download}
          >
            {exporting ? "Exportando…" : "Exportar CSV"}
          </button>
        </div>
      </div>
      {error && <p role="alert">{error}</p>}
      <section>
        <h2>Filtros</h2>
        <div className="coffee-grid coffee-filter-grid">
          <label>
            Cierre desde
            <input
              type="date"
              value={filters.from}
              max={filters.until || undefined}
              onChange={(e) => change("from", e.target.value)}
            />
          </label>
          <label>
            Cierre hasta
            <input
              type="date"
              value={filters.until}
              min={filters.from || undefined}
              onChange={(e) => change("until", e.target.value)}
            />
          </label>
          <label>
            Ruta
            <select
              value={filters.route}
              onChange={(e) => change("route", e.target.value)}
            >
              <option value="">Todas las rutas</option>
              {options.data?.routes.map((route) => (
                <option key={route}>{route}</option>
              ))}
            </select>
          </label>
          <label>
            Máquina
            <select
              value={filters.machine}
              onChange={(e) => change("machine", e.target.value)}
            >
              <option value="">Todas las máquinas</option>
              {options.data?.machines.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Operador
            <select
              value={filters.operator}
              onChange={(e) => change("operator", e.target.value)}
            >
              <option value="">Todos los operadores</option>
              {options.data?.operators.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="coffee-actions">
          <button
            onClick={() => {
              setFilters({
                from: "",
                until: "",
                route: "",
                machine: "",
                operator: "",
              });
              setPage(1);
            }}
          >
            Limpiar filtros
          </button>
          <button onClick={retry}>Actualizar</button>
        </div>
        {options.error && <p role="alert">{options.error}</p>}
      </section>
      {report.loading && <p role="status">Calculando estadísticas…</p>}
      {report.error && (
        <p role="alert">
          {report.error} <button onClick={retry}>Reintentar</button>
        </p>
      )}
      {report.data && (
        <>
          <div className="coffee-kpis">
            <section>
              <span>Consumo registrado</span>
              <strong>
                {report.data.totals.consumption.toLocaleString("es-PA")}
              </strong>
              <small>unidades · diferencia de contadores</small>
            </section>
            <section>
              <span>Total de ventas</span>
              <strong>{usd(report.data.totals.salesCents)}</strong>
              <small>
                {report.data.totals.salesReported} registros con importe
                reportado
              </small>
            </section>
            <section>
              <span>Registros de lectura</span>
              <strong>{report.data.totals.count}</strong>
              <small>Los reinicios no cuentan como lecturas</small>
            </section>
          </div>
          <CoffeeCharts report={report.data} />
        </>
      )}
      <section>
        <h2>Historial completo</h2>
        <p className="coffee-hint">
          Las auditorías se consultan en el detalle y la exportación; no forman
          parte de los totales.
        </p>
        <CoffeeHistory
          resource={history}
          page={page}
          setPage={setPage}
          onDetail={setDetail}
          onRetry={retry}
        />
      </section>
      {detail && (
        <CoffeeDetail
          key={detail}
          id={detail}
          internal
          request={request}
          onClose={() => setDetail(null)}
          onSaved={retry}
        />
      )}
      {reset && (
        <CoffeeReset
          request={request}
          onClose={() => setReset(false)}
          onSaved={retry}
        />
      )}
    </main>
  );
}
